import { select, put, call } from 'redux-saga/effects';
import finalize from '@gcc/pip-components/dist/utils/prerender/finalize';
import {
  updatePricing,
  clearPricing,
  getPricingFromThd,
  getPricingMsrp,
  getPricingSO,
  getPricingError,
  setPricingAvailable,
} from '../../actions/pricingActions';
import { updateFulfillmentMethod } from '../../actions/cartActions/_actionCreators';
import {
  skuMapSelector,
  configuratorSelector,
  thdStoreInfoSelector,
  configSelector,
  productDetailsAttributeSelector,
  productDetailsSelector,
} from '../../selectors';
import getThdProductDetailsAndPricing from './getThdProductDetailsAndPricing';
import getMsrpPricing from './getMsrpPricing';
import getSOPricing from './getSOPricing';
import getLineItemPricing from './getLineItemPricing';
import { hasError } from './_utils';
import getNextFulfillmentMethod from './_getNextFulfillmentMethod';
import { CUSTOM_CONFIG_BASED, PPE } from '../../utils/pipFiguratorType';
import { CONFIGURATOR } from '../../actions/hydration/constants';
import { setHydrationComplete } from '../../actions/hydration/orchestration';
import { trackErrorInteractions } from '../../utils/analytics/trackEvents';

export default function* getPricing() {
  const config = yield select(configSelector);
  const { storeNumber } = yield select(thdStoreInfoSelector);
  const skuMap = yield select(skuMapSelector);
  const { options, choices } = yield select(configuratorSelector);
  const { bypassSkuMap, pipfiguratorType, isMajorAppliance } = yield select(
    productDetailsAttributeSelector
  );

  const { productType } = yield select(productDetailsSelector);

  if (hasError(options, choices)) {
    yield put(clearPricing());
    return;
  }

  let path;
  try {
    // puts here are for debugging/testing
    // consider making sagas of these paths instead
    // (i.e. yield takeLatest() on their respective actions)

    if (skuMap.skuType === 'HomeDepot_OMSID') {
      yield put(getPricingFromThd());
      path = getThdProductDetailsAndPricing;
    } else if (config.useMSRP) {
      yield put(getPricingMsrp());
      path = getMsrpPricing;
    } else if (config.brand === 'Milwaukee') {
      path = getLineItemPricing;
    } else if (
      (bypassSkuMap && bypassSkuMap === 'True') ||
      (pipfiguratorType && pipfiguratorType === CUSTOM_CONFIG_BASED)
    ) {
      yield put(getPricingMsrp());
      path = getMsrpPricing;
    } else {
      yield put(getPricingSO());
      path = getSOPricing;
    }

    // TODO: see if we can move each path's updateProductInfo effect up here too.
    // getThdProductDetails fires off a few more actions after it, but
    // not sure if that's necessary.

    let updatedPricing;
    if (productType === PPE) {
      updatedPricing = yield call(path, skuMap, storeNumber);
    } else {
      updatedPricing = yield call(path, skuMap, storeNumber, options, choices);
    }

    const someSkusHaveZeroPrice = updatedPricing?.products?.some(
      (product) => product?.pricing?.originalPrice === 0
    );

    const numberOfRequestSkus = skuMap?.subSkus?.filter((sku) => sku.sku).length;
    const numberOfResponseSkus = updatedPricing?.products?.filter(
      (product) => product.itemId
    ).length;
    const clearPrice =
      isMajorAppliance === 'True' &&
      (someSkusHaveZeroPrice || numberOfRequestSkus !== numberOfResponseSkus);

    if (clearPrice) {
      yield put(clearPricing());
    } else {
      yield put(updatePricing(updatedPricing));
    }

    yield put(setPricingAvailable());

    const fulfillmentMethod = yield select(getNextFulfillmentMethod);
    yield put(updateFulfillmentMethod(fulfillmentMethod));

    // At the end of pricing call hydration is complete
    yield put(setHydrationComplete(CONFIGURATOR));

    if (window && window?.prerender) finalize();
  } catch (err) {
    if (err) {
      const errorObject = {
        type: 'system',
        displayType: 'inline',
        pageSection: 'Pricing',
        message: err.response?.statusText || 'An Internal Error has Occurred',
        code: err.response?.status || 'API did not respond',
      };
      if (productType) {
        trackErrorInteractions(errorObject, 'error', 'post page error');
      } else {
        trackErrorInteractions(errorObject);
      }
    }
    yield put(clearPricing());
    yield put(getPricingError(err));
  }
}
